<template>
  <b-modal
    :id="modalId"
    title="Manual Result Entry"
    size="lg"
    centered
    @shown="fetchRunners"
    body-class="modal-body-scrollable"
  >
    <div v-if="isLoading" class="text-center py-5">
      <b-spinner variant="primary" class="mb-2" />
      <div class="text-muted">Loading runners...</div>
    </div>
    <b-row v-else>
      <b-col cols="12">
        <b-row class="mb-1 align-items-end">
          <b-col cols="12" md="3">
            <h5 class="mb-0">Race Placings</h5>
          </b-col>
          <b-col md="9" class="d-none d-md-block" v-if="!isFuture">
            <b-row>
              <b-col md="2" v-for="f in dividendFields" :key="f.key">
                <small class="font-weight-bold text-muted">{{ f.label }}</small>
              </b-col>
            </b-row>
          </b-col>
        </b-row>
      </b-col>
      <b-col
        :cols="isFuture ? 12 : (row.finalPosition <= 4 ? 12 : 4)"
        v-for="row in results"
        :key="row.finalPosition"
        class="mb-1"
      >
        <b-row align-v="center" v-if="isFuture">
          <b-col cols="12" md="3">
            <small class="text-muted">
              {{ formatPlaceOrdinal(row.finalPosition) }} Place
              <span v-if="row.finalPosition <= 4" class="text-danger">*</span>
            </small>
          </b-col>
          <b-col cols="12" md="9">
            <div class="d-flex align-items-center" style="gap: 6px">
              <v-select
                class="flex-grow-1"
                v-model="row.runnerIds"
                :options="availableRunnerOptions(row)"
                :multiple="true"
                label="text"
                :reduce="(opt) => opt.value"
                placeholder="Select runners..."
                :close-on-select="false"
                @option:selected="(opt) => onRunnerAdded(row, opt)"
              >
                <template #option="opt">
                  <span :style="row.runnerIds.includes(opt.value) ? 'opacity: 0.5' : ''">
                    {{ opt.text }}
                  </span>
                </template>
                <template #selected-option="opt">
                  <span class="text-truncate" style="max-width: 120px">{{ opt.text }}</span>
                </template>
              </v-select>
              <b-button
                v-if="row.finalPosition > 4"
                variant="outline-danger"
                size="sm"
                @click="removePlace(row)"
              >✕</b-button>
            </div>
          </b-col>
        </b-row>
        <b-row align-v="center" v-else>
          <b-col cols="12" :md="row.finalPosition <= 4 ? 3 : 12">
            <small class="text-muted">{{ formatPlaceOrdinal(row.finalPosition) }} Place</small>
            <v-select
              v-model="row.runnerIds"
              :options="availableRunnerOptions(row)"
              :multiple="true"
              label="text"
              :reduce="(opt) => opt.value"
              placeholder="Select runners..."
              :close-on-select="false"
              @option:selected="(opt) => onRunnerAdded(row, opt)"
            >
              <template #option="opt">
                <span :style="row.runnerIds.includes(opt.value) ? 'opacity: 0.5' : ''">
                  {{ opt.text }}
                </span>
              </template>
              <template #selected-option="opt">
                <span class="text-truncate" style="max-width: 120px">{{ opt.text }}</span>
              </template>
            </v-select>
          </b-col>
          <b-col cols="12" md="9" v-if="row.finalPosition <= 4">
            <b-row>
              <b-col cols="6" md="2" v-for="f in dividendFields" :key="f.key">
                <small class="text-muted d-md-none">{{ f.label }}</small>
                <b-form-input
                  type="text"
                  :step="0.1"
                  :min="0"
                  v-model="row[f.key]"
                  @input="onDividendChange(row, f.key)"
                  :formatter="formatNumber"
                />
              </b-col>
            </b-row>
          </b-col>
        </b-row>
      </b-col>
      <b-col cols="12" v-if="isFuture" class="mt-1">
        <b-button variant="outline-secondary" size="sm" @click="addPlace">
          + Add next place
        </b-button>
      </b-col>
    </b-row>

    <!-- SECTION 2: Pool Dividends -->
    <b-row v-if="!isLoading && !isFuture" class="text-black">
      <b-col cols="12" class="mt-3">
        <hr />
        <h5 class="mb-3">Pool Dividends</h5>

        <!-- Exotics -->
        <div class="text-uppercase text-muted small font-weight-bold mb-2">Exotics</div>
        <div
          v-for="d in dividends.filter((d) => d.legCount === null)"
          :key="d.wageringProduct"
          class="border rounded p-2 mb-2"
        >
          <div
            v-for="(combo, ci) in d.poolDividends"
            :key="ci"
            class="d-flex align-items-center flex-wrap mb-1"
            style="gap: 6px"
          >
            <div style="min-width: 90px; font-weight: 600; font-size: 13px">
              {{ ci === 0 ? d.wageringProduct : '\xa0' }}
            </div>
            <div class="d-flex align-items-flex-end flex-wrap" style="gap: 4px; flex: 1">
              <div
                v-for="(sel, si) in combo.selections"
                :key="si"
                class="d-flex align-items-flex-end"
                style="gap: 4px"
              >
                <div class="d-flex flex-column align-items-center">
                  <span class="text-muted" style="font-size: 10px; line-height: 1.2">{{ formatPlaceOrdinal(si + 1) }}</span>
                  <b-form-select
                    :value="combo.selections[si]"
                    :options="runnerNoOptions"
                    value-field="value"
                    text-field="text"
                    size="sm"
                    style="width: 70px"
                    @change="(val) => $set(combo.selections, si, val ? Number(val) : null)"
                  />
                </div>
                <span
                  v-if="si < combo.selections.length - 1"
                  class="text-muted small mb-1"
                >→</span>
              </div>
            </div>
            <b-form-input
              type="text"
              :min="0"
              v-model="combo.amount"
              placeholder="Amount"
              style="width: 90px"
              size="sm"
              :formatter="formatNumber"
            />
            <b-button
              v-if="d.poolDividends.length > 1"
              variant="outline-danger"
              size="sm"
              @click="removeCombo(d, ci)"
            >✕</b-button>
            <div v-else style="width: 31px" />
          </div>
          <b-button
            variant="outline-secondary"
            size="sm"
            class="mt-1"
            @click="addCombo(d)"
          >+ Add Combo</b-button>
        </div>

        <!-- Quaddie Family -->
        <template v-if="visibleQuaddieDividends.length > 0">
        <div class="text-uppercase text-muted small font-weight-bold mb-1 mt-3">Quaddie Family</div>
        <p class="text-muted small mb-2">
          Enter runner numbers for each leg (manual — runners from other races)
        </p>
        <div
          v-for="d in visibleQuaddieDividends"
          :key="d.wageringProduct"
          class="border rounded p-2 mb-2"
        >
          <div
            v-for="(combo, ci) in d.poolDividends"
            :key="ci"
            class="d-flex align-items-center flex-wrap mb-1"
            style="gap: 6px"
          >
            <div style="min-width: 130px; font-weight: 600; font-size: 13px">
              {{ ci === 0 ? `${quaddieDisplayNames[d.wageringProduct] || d.wageringProduct} (${d.legCount} legs)` : '\xa0' }}
            </div>
            <div class="d-flex align-items-center flex-wrap" style="gap: 4px; flex: 1">
              <div
                v-for="(sel, si) in combo.selections"
                :key="si"
                class="d-flex align-items-center"
                style="gap: 4px"
              >
                <span class="text-muted small">L{{ si + 1 }}:</span>
                <b-form-input
                  type="text"
                  min="1"
                  :value="combo.selections[si]"
                  @input="(val) => $set(combo.selections, si, val ? Number(val) : null)"
                  style="width: 60px"
                  size="sm"
                  :formatter="formatNumber"
                />
              </div>
            </div>
            <b-form-input
              type="text"
              :min="0"
              v-model="combo.amount"
              placeholder="Amount"
              style="width: 90px"
              size="sm"
              :formatter="formatNumber"
            />
            <b-button
              v-if="d.poolDividends.length > 1"
              variant="outline-danger"
              size="sm"
              @click="removeCombo(d, ci)"
            >✕</b-button>
            <div v-else style="width: 31px" />
          </div>
          <b-button
            variant="outline-secondary"
            size="sm"
            class="mt-1"
            @click="addCombo(d)"
          >+ Add Combo</b-button>
        </div>
        </template>
      </b-col>
    </b-row>

    <!-- FOOTER -->
    <template #modal-footer>
      <div class="d-flex flex-wrap justify-content-end mt-1" style="gap: 6px">
        <b-button variant="secondary" @click="handleCloseModal">
          Cancel
        </b-button>
        <b-button variant="primary" @click="submitResults">
          Confirm Results
        </b-button>
      </div>
    </template>
  </b-modal>
</template>
<script>
import {
  BModal,
  BFormInput,
  BFormSelect,
  BButton,
  BRow,
  BCol,
  BSpinner,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { mapActions } from "vuex";
import useToastHandler from "@/services/toastHandler";
export default {
  components: {
    BModal,
    BFormInput,
    BFormSelect,
    BButton,
    BRow,
    BCol,
    BSpinner,
    vSelect,
  },
  props: {
    raceId: {
      type: String,
      default: null,
    },
    modalId: {
      type: String,
      default: "manual-result-modal",
    },
    isFuture: {
      type: Boolean,
      default: false,
    },
    meetingRaces: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isLoading: false,
      runners: [],
      results: [],
      dividendFields: [
        { key: "win", label: "Win" },
        { key: "place", label: "Place" },
        { key: "startingPrice", label: "SP" },
        { key: "bestDivSp", label: "Best Div SP" },
        { key: "midDiv", label: "Mid Div" },
        { key: "midDivPlace", label: "Mid Div Place" },
      ],
      dividends: [],
      visibleQuaddieProducts: null,
    };
  },
  computed: {
    runnerNoOptions() {
      return this.runners.map((r) => ({ value: r.runnerNo, text: String(r.runnerNo) }));
    },
    legNumberOptions() {
      return Array.from({ length: 30 }, (_, i) => i + 1);
    },
    quaddieDisplayNames() {
      return {
        EARLY_QUADDIE: "EARLY QUADDIE",
        QUAD: "QUADDIE",
        DAILY_DOUBLE: "DAILY DOUBLE",
        RUNNING_DOUBLE: "RUNNING DOUBLE",
        TREBLE: "TREBLE",
      };
    },
    visibleQuaddieDividends() {
      if (!this.visibleQuaddieProducts) return [];
      return this.dividends.filter(
        (d) => d.legCount !== null && this.visibleQuaddieProducts.includes(d.wageringProduct)
      );
    },
  },
  watch: {
    runners(newVal) {
      if (!newVal.length) return;
      const highestSaved = newVal.reduce((max, r) => {
        const pos = r.result?.resultPosition || r.finalPosition || 0;
        return pos > max ? pos : max;
      }, 0);
      const rowCount = this.isFuture
        ? Math.max(4, highestSaved)
        : newVal.length;
      const results = Array.from({ length: rowCount }, (_, idx) =>
        this.createResult(idx + 1)
      );
      newVal.forEach((runner) => {
        const pos = runner.result?.resultPosition || runner.finalPosition;
        if (!pos) return;
        const row = results.find((r) => r.finalPosition === pos);
        if (!row) return;
        row.runnerIds.push(runner.runnerId);
        if (!this.isFuture && row.runnerIds.length === 1 && runner.result) {
          this.dividendFields.forEach(({ key }) => {
            row[key] = runner.result[key] ?? null;
          });
        }
      });
      this.results = results;
    },
    results: {
      deep: true,
      handler() {
        this.syncExoticSelections();
      },
    },
  },
  created() {
    this.dividends = this.buildInitialDividends();
    this.apiLoadedProducts = new Set();
  },
  methods: {
    ...mapActions("trading/racing", [
      "getRunnersOfRace",
      "getDetailOfRace",
      "updateRaceResults",
      "recalculateRaceResults",
    ]),
    buildInitialDividends() {
      return [
        { wageringProduct: "QUINELLA",       legCount: null, poolDividends: [{ selections: [null, null],             amount: null }] },
        { wageringProduct: "EXACTA",         legCount: null, poolDividends: [{ selections: [null, null],             amount: null }] },
        { wageringProduct: "TRIFECTA",       legCount: null, poolDividends: [{ selections: [null, null, null],       amount: null }] },
        { wageringProduct: "FIRST_FOUR",     legCount: null, poolDividends: [{ selections: [null, null, null, null], amount: null }] },
        // { wageringProduct: "EARLY_QUADDIE",   legCount: 4,    poolDividends: [{ selections: [null, null, null, null], amount: null }] },
        { wageringProduct: "QUAD",           legCount: 4,    poolDividends: [{ selections: [null, null, null, null], amount: null }] },
        // { wageringProduct: "DAILY_DOUBLE",   legCount: 2,    poolDividends: [{ selections: [null, null],             amount: null }] },
        // { wageringProduct: "RUNNING_DOUBLE", legCount: 2,    poolDividends: [{ selections: [null, null],             amount: null }] },
        // { wageringProduct: "TREBLE",         legCount: 3,    poolDividends: [{ selections: [null, null, null],       amount: null }] },
      ];
    },
    syncExoticSelections() {
      if (this.isFuture) return;
      // Flatten top-4 runners: sort by finalPosition, then runnerNo within same position
      const top4 = [];
      [...this.results]
        .filter((r) => r.finalPosition <= 4 && r.runnerIds.length > 0)
        .sort((a, b) => a.finalPosition - b.finalPosition)
        .forEach((row) => {
          row.runnerIds
            .map((id) => this.runners.find((r) => r.runnerId === id))
            .filter(Boolean)
            .sort((a, b) => a.runnerNo - b.runnerNo)
            .forEach((runner) => {
              if (top4.length < 4) top4.push(runner.runnerNo);
            });
        });

      const slotCount = { QUINELLA: 2, EXACTA: 2, TRIFECTA: 3, FIRST_FOUR: 4 };
      this.dividends.forEach((d) => {
        if (d.legCount !== null) return;
        if (this.apiLoadedProducts.has(d.wageringProduct)) return;
        const count = slotCount[d.wageringProduct];
        if (!count || !d.poolDividends[0]) return;
        const newSelections = Array.from({ length: count }, (_, i) => top4[i] ?? null);
        this.$set(d.poolDividends[0], "selections", newSelections);
      });
    },
    addCombo(dividend) {
      const selLen = dividend.poolDividends[0].selections.length;
      dividend.poolDividends.push({
        selections: Array(selLen).fill(null),
        amount: null,
      });
    },
    addPlace() {
      this.results.push(this.createResult(this.results.length + 1));
    },
    removePlace(row) {
      if (row.finalPosition <= 4) return;
      this.results = this.results
        .filter((r) => r.finalPosition !== row.finalPosition)
        .map((r, idx) => ({ ...r, finalPosition: idx + 1 }));
    },
    removeCombo(dividend, index) {
      dividend.poolDividends.splice(index, 1);
    },
    async fetchRunners() {
      try {
        this.isLoading = true;
        const raceId = this.raceId || this.$route.query.raceId;
        const [data, raceDetail] = await Promise.all([
          this.getRunnersOfRace(raceId),
          this.getDetailOfRace(raceId),
        ]);
        if (!data) return;
        this.runners = data
          .filter((r) => !r.scratched)
          .map((r) => ({
            ...r,
            value: r.runnerId,
            text: r.runnerNo ? `${r.runnerNo}. ${r.runnerName}` : r.runnerName,
            result: r.result || {},
          }));
        if (raceDetail) {
          this.fillDividendsFromDetail(raceDetail);
          this.computeVisibleQuaddies(raceDetail);
          this.fillQuaddieLegSelections(raceDetail);
        }
      } catch (error) {
      } finally {
        this.isLoading = false;
      }
    },
    computeVisibleQuaddies(raceDetail) {
      const betTypes = raceDetail.betTypes;
      if (!betTypes || !betTypes.length) {
        this.visibleQuaddieProducts = [];
        return;
      }
      const raceNo = raceDetail.eventNo;
      const visible = new Set();
      betTypes.forEach((bt) => {
        if (bt.betType !== "lateQuaddie") return;
        const lastLeg = Math.max(...bt.legs);
        if (raceNo === lastLeg) visible.add("QUAD");
      });
      this.visibleQuaddieProducts = [...visible];
    },
    fillQuaddieLegSelections(raceDetail) {
      if (!this.meetingRaces || !this.meetingRaces.length) return;
      const betTypes = raceDetail.betTypes;
      if (!betTypes || !betTypes.length) return;

      for (const bt of betTypes) {
        if (bt.betType !== "lateQuaddie") continue;
        const product = "QUAD";
        if (!product) continue;
        if (!this.visibleQuaddieProducts || !this.visibleQuaddieProducts.includes(product)) continue;
        if (this.apiLoadedProducts.has(product)) continue;

        const dividend = this.dividends.find((d) => d.wageringProduct === product);
        if (!dividend || !dividend.poolDividends[0]) continue;

        const legSelections = bt.legs.map((legEventNo) => {
          const raceItem = this.meetingRaces.find((r) => r.eventNo === legEventNo);
          const winner = (raceItem?.results || []).find((r) => r.finalPosition === 1);
          return winner ? winner.runnerNo : null;
        });

        this.$set(dividend.poolDividends[0], "selections", legSelections);
      }
    },
    fillDividendsFromDetail(raceDetail) {
      const incoming = raceDetail.dividends;
      if (!incoming || !incoming.length) return;
      const byProduct = {};
      incoming.forEach((item) => {
        if (!byProduct[item.product]) byProduct[item.product] = [];
        byProduct[item.product].push({
          selections: [...item.selections],
          amount: item.amount,
        });
      });
      this.dividends.forEach((d) => {
        const combos = byProduct[d.wageringProduct];
        if (!combos || !combos.length) return;
        this.$set(d, "poolDividends", combos);
        this.apiLoadedProducts.add(d.wageringProduct);
      });
    },
    availableRunnerOptions(row) {
      const selectedElsewhere = this.results
        .filter((r) => r.finalPosition !== row.finalPosition)
        .flatMap((r) => r.runnerIds);
      return this.runners
        .filter((r) => !selectedElsewhere.includes(r.runnerId))
        .map((r) => ({ value: r.runnerId, text: r.text }));
    },
    onRunnerAdded(row, opt) {
      if (row.runnerIds.length === 1) {
        const runner = this.runners.find((r) => r.runnerId === opt.value);
        if (runner?.result) this.fillRowFromRunner(row, runner);
      }
    },
    fillRowFromRunner(row, runner) {
      this.dividendFields.forEach(({ key }) => {
        row[key] = runner.result[key] ?? null;
      });
    },
    onDividendChange(row, field) {
      row.runnerIds.forEach((runnerId) => {
        const runner = this.runners.find((r) => r.runnerId === runnerId);
        if (runner)
          runner.result[field] =
            row[field] !== null ? Number(row[field]) : null;
      });
    },
    async submitResults() {
      try {
        const raceId = this.raceId || this.$route.query.raceId;
        if (this.isFuture) {
          const top4 = this.results.filter((r) => r.finalPosition <= 4);
          if (top4.length < 4 || top4.some((r) => r.runnerIds.length === 0)) {
            this.showToastError(
              "The first 4 finishing positions are required"
            );
            return;
          }
          const results = [...this.results]
            .filter((r) => r.runnerIds.length > 0)
            .sort((a, b) => a.finalPosition - b.finalPosition)
            .flatMap((r) =>
              r.runnerIds.map((runnerId) => ({
                finalPosition: r.finalPosition,
                runnerId,
              }))
            );
          const ok = await this.updateRaceResults({
            raceId,
            results,
            dividends: [],
          });
          if (ok) {
            await this.recalculateRaceResults(raceId);
            this.showToastSuccess("Race results updated successfully");
            this.$emit("success");
            this.handleCloseModal();
          }
          return;
        }
        const totalAssigned = this.results.reduce(
          (sum, r) => sum + r.runnerIds.length,
          0
        );
        if (totalAssigned !== this.runners.length) {
          this.showToastError(
            "All runners must be assigned a finishing position"
          );
          return;
        }
        if (
          !this.results.some(
            (r) => r.runnerIds.length > 0 && r.finalPosition === 1
          )
        ) {
          this.showToastError("1st place must have at least one runner");
          return;
        }

        const results = [...this.results]
          .sort((a, b) => a.finalPosition - b.finalPosition)
          .flatMap((r) =>
            r.runnerIds.map((runnerId) => ({
              finalPosition: r.finalPosition,
              runnerId,
              ...this.pickPerRunnerDividends(r),
            }))
          );

        const visibleQuaddies = this.visibleQuaddieProducts || [];
        const dividends = this.dividends
          .filter((d) => d.legCount === null || visibleQuaddies.includes(d.wageringProduct))
          .map((d) => ({
            wageringProduct: d.wageringProduct,
            poolDividends: d.poolDividends
              .filter(
                (c) =>
                  Number(c.amount) > 0 &&
                  c.selections.every((s) => s !== null && s !== "")
              )
              .map((c) => ({
                selections: c.selections.map(Number),
                amount: Number(c.amount),
              })),
          }))
          .filter((d) => d.poolDividends.length > 0);
        
        const ok = await this.updateRaceResults({ raceId, results, dividends });
        if (ok) {
          await this.recalculateRaceResults(raceId);
          this.showToastSuccess("Race results updated successfully");
          this.$emit("success");
          this.handleCloseModal();
        }
      } catch (e) {
        console.error("Submit results failed", e);
      }
    },
    pickPerRunnerDividends(row) {
      const fields = ["startingPrice", "bestDivSp", "midDiv", "midDivPlace", "win", "place"];
      return Object.fromEntries(
        fields
          .map((key) => [key, Number(row[key]) || null])
          .filter(([, v]) => v !== null)
      );
    },
    createResult(pos) {
      const base = {
        finalPosition: pos,
        runnerIds: [],
      };
      if (pos > 4) return base;
      return {
        ...base,
        startingPrice: null,
        bestDivSp: null,
        midDiv: null,
        midDivPlace: null,
        win: null,
        place: null,
      };
    },
    resetRow(row) {
      const fresh = this.createResult(row.finalPosition);
      Object.keys(fresh).forEach((k) => {
        row[k] = fresh[k];
      });
    },
    formatPlaceOrdinal(n) {
      return `${n}${["st", "nd", "rd"][n - 1] || "th"}`;
    },
    handleCloseModal() {
      this.$bvModal.hide(this.modalId);
      this.runners = [];
      this.results = [];
      this.dividends = this.buildInitialDividends();
      this.visibleQuaddieProducts = null;
      this.apiLoadedProducts = new Set();
    },
    formatNumber(value) {
      const regexNumber = /^[0-9.]+$/;

      if (regexNumber.test(value)) {
        if (value.split("").filter((s) => s === ".").length > 1) return;
        return value;
      }
      value = value.replace(/[^0-9.]/, "");
      return value;
    },
  },
  setup() {
    const { showToastSuccess, showToastError } = useToastHandler();
    return { showToastSuccess, showToastError };
  },
};
</script>

<style scoped>
.modal-body-scrollable {
  max-height: 70vh;
  overflow-y: auto;
}
</style>
